import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DataSessionService } from '../../../core/services/data-session.service';
import { ModalData, ModalOption } from '../../shared/type/modal';
import { Observable, of } from 'rxjs';
import { SelectData } from '../../shared/type/input';
import { ButtonPopoverComponent } from '../../shared/ui/button-popover/button-popover.component';
import { PopoverController } from '@ionic/angular/standalone';

@Injectable({ providedIn: 'root' })
export class ManagementsService {
  private readonly _http = inject(HttpClient);
  private readonly _dataSessionService = inject(DataSessionService);
  public readonly popoverController = inject(PopoverController);

  userModalData = (action: () => void): ModalOption => {
    return {
      option: { label: 'Archivar', action: () => action() },
    };
  };

  errorRequestModalData = (action: () => void): ModalData => {
    return {
      icon: 'icon-circulo-error',
      title: 'No tiene permisos para rechazar',
      rigthButton: { label: 'error.modal.close', action: () => action() },
    };
  };

  requestModalData = (
    accept: () => void,
    cancel: () => void,
    state: number,
    showComments?: () => void
  ): ModalOption => {
    const aceptar = { label: 'Aceptar solicitud', action: () => accept() };
    const rechazar = { label: 'Rechazar solicitud', action: () => cancel() };
    const comments = {
      label: 'Ver observaciones',
      action: () => showComments && showComments(),
    };

    const optionsMap: { [key: number]: ModalOption } = {
      0: {
        option: aceptar,
        option2: rechazar,
        ...(showComments && { option3: comments }),
      },
      1: { option: rechazar, ...(showComments && { option2: comments }) },
      2: { option: aceptar, ...(showComments && { option2: comments }) },
    };

    return (
      optionsMap[state] || {
        option: { label: '', action: () => {} },
        option2: { label: '', action: () => {} },
      }
    );
  };

  statusUserData: SelectData<StatusRequestValue>[] = [
    { value: '0', label: 'Pendiente' },
    { value: '1', label: 'Activo' },
    { value: '2', label: 'Rechazado' },
    { value: '3', label: 'En curso' },
    { value: '-1', label: 'Archivado' },
  ];

  statusRequestData: SelectData<StatusRequestValue>[] = [
    { value: '0', label: 'Pendiente' },
    { value: '1', label: 'Aceptado' },
    { value: '2', label: 'Rechazado' },
  ];

  archiveModalData = (
    cancelAction: () => void,
    continueAction: () => void
  ): ModalData => {
    return {
      icon: 'icon-information-circle',
      title: 'Archivar usuario',
      subtitle: '¿Está seguro que quiere archivar el usuario seleccionado?',
      dismissButton: { label: 'Cerrar', action: () => cancelAction() },
      leftButton: { label: 'No, cancelar', action: () => cancelAction() },
      rigthButton: { label: 'Si, continuar', action: () => continueAction() },
    };
  };

  acceptRequestModalData = (
    cancelAction: () => void,
    continueAction: () => void
  ): ModalData => {
    return {
      icon: 'icon-information-circle',
      title: 'Aceptar solicitud',
      subtitle:
        '¿Está seguro que quiere aceptar el acceso al usuario seleccionado?',
      dismissButton: { label: 'Cerrar', action: () => cancelAction() },
      leftButton: { label: 'No, cancelar', action: () => cancelAction() },
      rigthButton: { label: 'Si, continuar', action: () => continueAction() },
    };
  };

  declineRequestModalData = (
    cancelAction: () => void,
    continueAction: () => void
  ): ModalData => {
    return {
      icon: 'icon-information-circle',
      title: 'Rechazar solicitud',
      subtitle:
        '¿Está seguro que quiere rechazar el acceso al usuario seleccionado?',
      dismissButton: { label: 'Cerrar', action: () => cancelAction() },
      leftButton: { label: 'No, cancelar', action: () => cancelAction() },
      rigthButton: { label: 'Si, continuar', action: () => continueAction() },
    };
  };

  showCommentsModalData = (comments: string): ModalData => {
    return {
      icon: 'icon-information-circle',
      title: 'Observaciones',
      subtitle: comments,
      dismissButton: { label: 'Cerrar', action: () => {} },
      leftButton: { label: 'Volver', action: () => {} },
    };
  };

  async openPopover(e: Event, modalOption: ModalOption) {
    const popover = await this.popoverController.create({
      component: ButtonPopoverComponent,
      componentProps: {
        modalOption: modalOption,
      },
      event: e,
    });

    await popover.present();
    return popover;
  }

  getFiltersRequests(): Observable<FiltersResponse> {
    return this._http.get<FiltersResponse>(
      `${this._dataSessionService.baseUrl}/supervises/filters`
    );
  }

  getSupervisesRequests(
    params?: FiltersParams
  ): Observable<SupervisesResponse> {
    const url = `${this._dataSessionService.baseUrl}/supervises/requests`;

    return this._http.get<SupervisesResponse>(url, {
      params: this.buildHttpParams(params),
    });
  }

  getManageUserExt = (
    params?: FiltersParams
  ): Observable<GetUsersExtResponse> => {
    return this._http.get<GetUsersExtResponse>(
      `${this._dataSessionService.baseUrl}/manage/users-ext`,
      {
        params: this.buildHttpParams(params),
      }
    );
  };

  postSupervisesRequests = (body: SuperviseRequestBody): Observable<any> => {
    return this._http.post<any>(
      `${this._dataSessionService.baseUrl}/supervises/requests`,
      body
    );
  };

  postManageUserExt = (email: string): Observable<any> => {
    const body = {
      user: email,
    };
    return this._http.post<any>(
      `${this._dataSessionService.baseUrl}/manage/user-ext`,
      body
    );
  };

  private buildHttpParams(params?: FiltersParams): { [key: string]: string } {
    if (!params) {
      return {};
    }

    const httpParams: { [key: string]: string } = {};

    if (params.userEmail) {
      httpParams['userEmail'] = params.userEmail;
    }
    if (params.userCompany) {
      httpParams['usercompany'] = params.userCompany;
    }
    if (params.state !== undefined && params.state !== null) {
      httpParams['state'] = params.state.toString();
    }
    if (params.content) {
      httpParams['content'] = params.content;
    }
    if (params.pageId !== undefined && params.pageId !== null) {
      httpParams['pageId'] = params.pageId.toString();
    }
    if (params.pageSize !== undefined && params.pageSize !== null) {
      httpParams['pageSize'] = params.pageSize.toString();
    }

    return httpParams;
  }
}
